import { useState, useEffect } from 'react';
import { useSettings, StoreManager } from '@/contexts/SettingsContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader, UserPlus, Trash, UserCog } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

import { STORE_PERMISSIONS } from '@/lib/permissions';

const PERMISSIONS = STORE_PERMISSIONS;

export function StoreManagersSettings() {
  const { managers, isLoading, isUpdating, getManagers, inviteManager, removeManager, updateManagerPermissions } = useSettings();
  const { currentStore } = useAuth();
  
  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>(['dashboard']);
  const [selectedManager, setSelectedManager] = useState<StoreManager | null>(null);
  const [managerToDelete, setManagerToDelete] = useState<string | null>(null);
  
  const handleInvite = async () => {
    if (!inviteEmail) return;
    
    try {
      await inviteManager(inviteEmail, selectedPermissions);
      setInviteEmail('');
      setSelectedPermissions([]);
      setIsInviteDialogOpen(false);
    } catch (error) {
      console.error('Error inviting manager:', error);
    }
  };
  
  const handleOpenEditDialog = (manager: StoreManager) => {
    setSelectedManager(manager);
    const permissions = manager.storePermissions || [];
    if (!permissions.includes('dashboard')) {
      permissions.push('dashboard');
    }
    setSelectedPermissions(permissions);
    setIsEditDialogOpen(true);
  };
  
  const handleUpdatePermissions = async () => {
    if (!selectedManager) return;
    
    try {
      await updateManagerPermissions(selectedManager._id, selectedPermissions);
      setSelectedManager(null);
      setSelectedPermissions([]);
      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Error updating permissions:', error);
    }
  };
  
  const handleOpenDeleteDialog = (managerId: string) => {
    setManagerToDelete(managerId);
    setIsDeleteDialogOpen(true);
  };
  
  const handleRemoveManager = async () => {
    if (!managerToDelete) return;
    
    try {
      await removeManager(managerToDelete);
      setManagerToDelete(null);
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Error removing manager:', error);
    }
  };
  
  const handlePermissionChange = (permissionId: string) => {
    if (permissionId === 'dashboard') return;
    
    setSelectedPermissions(prev => {
      if (prev.includes(permissionId)) {
        return prev.filter(id => id !== permissionId);
      } else {
        return [...prev, permissionId];
      }
    });
  };

  useEffect(() => {
    if (!selectedPermissions.includes('dashboard')) {
      setSelectedPermissions(prev => [...prev, 'dashboard']);
    }
  }, [selectedPermissions]);
  
  // Check if user is owner of current store
  const isStoreOwner = currentStore?.isOwner;
  
  if (!isStoreOwner) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Store Managers</CardTitle>
          <CardDescription>
            Only store owners can manage store managers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <p>You don't have permission to manage store managers.</p>
            <p className="text-sm mt-1">
              Contact the store owner for assistance.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Store Managers</CardTitle>
          <CardDescription>
            Invite others to help manage your store
          </CardDescription>
        </div>
        <Dialog open={isInviteDialogOpen} onOpenChange={setIsInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className="ml-auto" size="sm">
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Manager
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Invite Store Manager</DialogTitle>
              <DialogDescription>
                Invite someone to help manage your store. They will receive an email invitation.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  placeholder="manager@example.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="space-y-2">
                  {PERMISSIONS.map((permission) => (
                    <div key={permission.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`permission-${permission.id}`}
                        checked={selectedPermissions.includes(permission.id)}
                        onCheckedChange={() => handlePermissionChange(permission.id)}
                        disabled={permission.required}
                      />
                      <label
                        htmlFor={`permission-${permission.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {permission.label}{permission.required && " (Required)"}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsInviteDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={!inviteEmail || isUpdating}>
                {isUpdating ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Invitation'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Manager Permissions</DialogTitle>
              <DialogDescription>
                Update what {selectedManager?.name || selectedManager?.email} can access in your store.
                They will be notified by email about these changes.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="space-y-2">
                  {PERMISSIONS.map((permission) => (
                    <div key={permission.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-permission-${permission.id}`}
                        checked={selectedPermissions.includes(permission.id)}
                        onCheckedChange={() => handlePermissionChange(permission.id)}
                        disabled={permission.required}
                      />
                      <label
                        htmlFor={`edit-permission-${permission.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        {permission.label}{permission.required && " (Required)"}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdatePermissions} disabled={isUpdating}>
                {isUpdating ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  'Update Permissions'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove Manager</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to remove this manager? 
                They will no longer have access to manage your store.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleRemoveManager}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {isUpdating ? (
                  <>
                    <Loader className="mr-2 h-4 w-4 animate-spin" />
                    Removing...
                  </>
                ) : (
                  'Remove Manager'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <Loader className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : managers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>You haven't invited any managers yet.</p>
            <p className="text-sm mt-1">
              Invite team members to help manage your store.
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name / Email</TableHead>
                <TableHead>Permissions</TableHead>
                <TableHead className="w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {managers.map((manager) => (
                <TableRow key={manager._id}>
                  <TableCell>
                    <div>
                      <p className="font-medium">{manager.name || 'No name'}</p>
                      <p className="text-sm text-muted-foreground">{manager.email}</p>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {manager.storePermissions && manager.storePermissions.length > 0 ? (
                        manager.storePermissions.map((permission) => (
                          <div
                            key={permission}
                            className="rounded-full bg-secondary px-2 py-1 text-xs font-medium text-secondary-foreground"
                          >
                            {PERMISSIONS.find(p => p.id === permission)?.label || permission}
                          </div>
                        ))
                      ) : (
                        <span className="text-xs text-muted-foreground">No permissions</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenEditDialog(manager)}
                      >
                        <UserCog className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDeleteDialog(manager._id)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
} 